import PropTypes from 'prop-types';
import './fileViewer.css';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { buildBlankFileViewerUrl } from 'utils/urlUtils';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { useQueryData } from 'hook/useQueryData';
import { useGetContentQuery } from 'services/entityApi';
import { useSelector } from 'react-redux';

/**
 * FileViewer is a component that displays file content in an iframe using pdfjs.
 * The source URL for the iframe is built using the provided file content.
 * The file name is used as the title for the iframe.
 *
 * @component
 * @example
 * // Suppose fileContent is a Blob representing the file content and fileName is 'example.txt'
 * <FileViewer fileContent={fileContentBlob} fileName='example.txt' />
 *
 * @param {object} document - The document to be displayed.
 *
 * @returns {React.Component} The FileViewer component which displays the file content within an iframe.
 */
function FileViewer({ document }) {
  const { getValue: conf } = useConf(useGetConfigurationQuery);
  const pdfjsPreferences = conf('module.fileViewer.pdfjs');
  if (pdfjsPreferences != null) {
    localStorage.setItem('pdfjs.preferences', JSON.stringify(pdfjsPreferences));
  }

  const { contentId, label } = document;

  const [iframeReady, setIframeReady] = useState(false);

  const iframeRef = useRef();
  const iframe = iframeRef.current;

  const fileViewerSearchQuery = useSelector((state) => state.fileViewer.searchQuery);

  useEffect(() => {
    if (!iframe) {
      setIframeReady(false);
    }
  }, [iframe]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === 'viewerReady') {
        setIframeReady(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const { loading: contentLoading, data: content } = useQueryData(
    useGetContentQuery,
    {
      contentId,
      mimeType: 'application/pdf'
    },
    undefined,
    null
  );

  useEffect(() => {
    if (iframeReady && !contentLoading && content) {
      iframe.contentWindow.postMessage(content, '*');
    }
  }, [content, contentLoading, iframe, iframeReady]);

  if (contentLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <iframe
      id="fileViewerFrame"
      ref={iframeRef}
      title={label ?? ''}
      src={buildBlankFileViewerUrl(fileViewerSearchQuery)}
      className="fileViewerFrame"
      allowFullScreen
    />
  );
}

FileViewer.propTypes = {
  document: PropTypes.shape({
    contentId: PropTypes.string.isRequired,
    label: PropTypes.string
  })
};
export default FileViewer;
